import { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, User, Phone, Mail, FileText, Building2, Filter } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { NotesDialog } from './NotesDialog';
import { filterCustomers, organizationsByUser, type DirectoryUser, type Membership } from '@/lib/customers/customerDirectory';

// Reload the patient list at most once a minute while the doctor keeps searching.
const DIRECTORY_MAX_AGE_MS = 60_000;

interface Directory {
  users: DirectoryUser[];
  organizationsByUser: Map<string, string[]>;
  loadedAt: number;
}

async function loadDirectory(): Promise<Directory> {
  const [usersSnap, membersSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(query(collection(db, 'organizationMembers'), where('role', '==', 'customer'))),
  ]);
  return {
    users: usersSnap.docs.map((d) => ({ ...(d.data() as DirectoryUser), id: d.id })),
    organizationsByUser: organizationsByUser(membersSnap.docs.map((d) => d.data() as Membership)),
    loadedAt: Date.now(),
  };
}

interface Customer {
  id: string;
  firstNameEn?: string;
  lastNameEn?: string;
  firstNameAr?: string;
  lastNameAr?: string;
  email?: string;
  phone?: string;
  organizationName?: string;
}

interface Organization {
  id: string;
  name: string;
  nameAr?: string;
}

interface CustomerSearchProps {
  isAdmin: boolean;
}

export function CustomerSearch({ isAdmin }: CustomerSearchProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const directory = useRef<Directory | null>(null);
  const { currentUser } = useAuth();
  const { t, language, dir } = useLanguage();

  // Fetch organizations on component mount (admins only)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchOrganizations = async () => {
      try {
        const orgsQuery = query(collection(db, 'organizations'), orderBy('name'));
        const orgsSnapshot = await getDocs(orgsQuery);
        const orgsData = orgsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Organization[];
        setOrganizations(orgsData);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };

    fetchOrganizations();
  }, [isAdmin]);

  const searchCustomers = async () => {
    if (!searchTerm.trim() && selectedOrganization === 'all') {
      setCustomers([]);
      return;
    }

    try {
      setLoading(true);
      if (!directory.current || Date.now() - directory.current.loadedAt > DIRECTORY_MAX_AGE_MS) {
        directory.current = await loadDirectory();
      }
      const { users, organizationsByUser: orgsByUser } = directory.current;

      const matches = filterCustomers(users, {
        term: searchTerm,
        organizationId: selectedOrganization,
        organizationsByUser: orgsByUser,
      });

      setCustomers(matches.map((customer) => {
        const orgId = selectedOrganization !== 'all' ? selectedOrganization : orgsByUser.get(customer.id)?.[0];
        const org = organizations.find(o => o.id === orgId);
        return {
          ...customer,
          organizationName: org ? (language === 'ar' ? org.nameAr || org.name : org.name) : undefined
        };
      }));
    } catch (error) {
      console.error('Error searching customers:', error);
      toast.error(t('dashboard.coach.customerSearch.errors.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    const debounceTimer = setTimeout(() => {
      searchCustomers();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedOrganization, organizations, language, isAdmin]);

  // Only show this component for admin users. After the hooks, so a change
  // of isAdmin never changes the number of hooks React sees.
  if (!isAdmin) {
    return null;
  }

  const handleOpenCustomerFile = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowNotesDialog(true);
  };

  const getCustomerName = (customer: Customer) => {
    if (language === 'ar') {
      return `${customer.firstNameAr || ''} ${customer.lastNameAr || ''}`.trim() || 
             `${customer.firstNameEn || ''} ${customer.lastNameEn || ''}`.trim();
    }
    return `${customer.firstNameEn || ''} ${customer.lastNameEn || ''}`.trim() ||
           `${customer.firstNameAr || ''} ${customer.lastNameAr || ''}`.trim();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedOrganization('all');
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-3 md:p-4 lg:p-6">
        <CardTitle className="flex items-center gap-1.5 md:gap-2 text-base md:text-lg">
          <Search className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
          {t('dashboard.coach.customerSearch.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-4 lg:p-6 pt-0 space-y-3 md:space-y-4">
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3.5 w-3.5 md:h-4 md:w-4" />
            <Input
              placeholder={t('dashboard.coach.customerSearch.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 md:pl-10 h-8 md:h-9 text-xs md:text-sm"
              dir={dir}
            />
          </div>
          <Select value={selectedOrganization} onValueChange={setSelectedOrganization}>
            <SelectTrigger className="w-full sm:w-[180px] md:w-[200px] h-8 md:h-9 text-xs md:text-sm">
              <SelectValue placeholder={t('dashboard.coach.customerSearch.selectOrganization')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.coach.customerSearch.allOrganizations')}</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {language === 'ar' ? org.nameAr || org.name : org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={clearFilters}
            size="icon"
            className="h-8 w-8 md:h-9 md:w-9 flex-shrink-0"
          >
            <Filter className="h-3.5 w-3.5 md:h-4 md:w-4" />
          </Button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {!loading && (searchTerm || selectedOrganization !== 'all') && customers.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {t('dashboard.coach.customerSearch.noResults')}
          </div>
        )}

        {!loading && customers.length > 0 && (
          <div className="space-y-2 md:space-y-3">
            <div className="text-xs md:text-sm text-muted-foreground">
              {t('dashboard.coach.customerSearch.resultsCount', { count: customers.length })}
            </div>
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="flex flex-col gap-2 p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-sm md:text-base truncate">{getCustomerName(customer)}</h4>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-muted-foreground">
                      {customer.email && (
                        <div className="flex items-center gap-1 min-w-0">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                      {customer.organizationName && (
                        <div className="flex items-center gap-1 min-w-0">
                          <Building2 className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{customer.organizationName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 md:gap-2">
                  <Button
                    onClick={() => handleOpenCustomerFile(customer)}
                    variant="outline"
                    size="sm"
                    className="w-full text-xs md:text-sm h-8 md:h-9"
                  >
                    <FileText className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5" />
                    {t('dashboard.coach.customerSearch.openFile')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {showNotesDialog && selectedCustomer && (
        <NotesDialog
          appointment={{
            id: `search-${selectedCustomer.id}`,
            customerId: selectedCustomer.id,
            customerName: getCustomerName(selectedCustomer),
            customerPhone: selectedCustomer.phone,
            date: new Date(),
            coachId: currentUser?.uid || '',
            status: 'scheduled'
          }}
          onOpenChange={setShowNotesDialog}
          onNotesUpdate={() => {
            // Notes updated - no need to refresh customer list
            console.log('Notes updated for customer:', selectedCustomer.id);
          }}
        />
      )}
    </Card>
  );
}
